import { dateKey } from '../track/rng';

export interface GrandPrixStage {
  key: string;
  label: string;
}

export type GrandPrixFormat = 'race' | 'quali';

export interface GrandPrixRun {
  stages: GrandPrixStage[];
  index: number;
  /** `'quali'` runs an elimination qualifying session before every stage and
   *  grids the race from its result; `'race'` is the pace-estimated grid. */
  format: GrandPrixFormat;
  /** The player's final qualifying position at each stage, or null. Only ever
   *  set in `'quali'` format. Kept for the Grand Prix summary. */
  qualiPositions: (number | null)[];
  /** Grid order a driven qualifying session produced for each stage, consumed
   *  by `buildAIGrid`'s `gridOrder`. Null until that stage's quali is done. */
  gridOrders: ({ playerRow: number; aiRows: number[] } | null)[];
  /** Best valid lap time recorded for each stage so far, or null if not yet set. */
  times: (number | null)[];
  /** Best (highest-scoring) finish position at each stage, 1 = first, or null. */
  positions: (number | null)[];
  /** Championship points for that best finish, kept in step with `positions`. */
  points: (number | null)[];
  /** AI opponents on the grid. Fixed for the whole run once it starts. */
  gridSize: number;
  /** Room this run is scored against, or null for the AI-grid solo format.
   *  Room-mode stages never populate `positions`/`points` locally — those come
   *  from the server's room standings, computed from real friends' verified
   *  times, not a local AI-relative finish. */
  roomCode: string | null;
}

const STAGE_COUNT = 3;

export const GRID_SIZES = [5, 7, 11] as const;
export type GridSize = (typeof GRID_SIZES)[number];
export const DEFAULT_GRID_SIZE: GridSize = 7;

/** Standard F1-style scoring table; any position beyond it scores nothing. */
const POINTS_TABLE = [25, 18, 15, 12, 10, 8, 6, 4, 2, 1];

/** Points for a 1-based finishing position. */
export function pointsForPosition(position: number): number {
  return POINTS_TABLE[position - 1] ?? 0;
}

/**
 * Today plus the two days before it, oldest first, today last. Every stage is
 * an ordinary daily circuit — nothing invented for the mode — so a Grand Prix
 * works fully offline and needs no storage beyond each stage's normal best time.
 */
export function buildGrandPrixStages(today: Date = new Date(), count = STAGE_COUNT): GrandPrixStage[] {
  const stages: GrandPrixStage[] = [];
  for (let i = count - 1; i >= 0; i--) {
    const key = dateKey(new Date(today.getTime() - i * 86400000));
    stages.push({ key, label: i === 0 ? 'Today' : key });
  }
  return stages;
}

export function createGrandPrix(
  today: Date = new Date(),
  gridSize: GridSize = DEFAULT_GRID_SIZE,
  roomCode: string | null = null,
  format: GrandPrixFormat = 'race',
): GrandPrixRun {
  const stages = buildGrandPrixStages(today);
  return {
    stages,
    index: 0,
    format,
    qualiPositions: stages.map(() => null),
    gridOrders: stages.map(() => null),
    times: stages.map(() => null),
    positions: stages.map(() => null),
    points: stages.map(() => null),
    gridSize,
    roomCode,
  };
}

/**
 * Records one race attempt at the current stage, keeping whichever result is
 * better — ranked by points first, lap time as the tiebreak — the same way a
 * personal best only ever improves on a retry.
 */
export function recordGrandPrixAttempt(run: GrandPrixRun, timeMs: number, position: number): void {
  const points = pointsForPosition(position);
  const bestPoints = run.points[run.index];
  const bestTime = run.times[run.index];

  const better =
    bestPoints === null ||
    points > bestPoints ||
    (points === bestPoints && (bestTime === null || timeMs < bestTime));
  if (better) {
    run.points[run.index] = points;
    run.positions[run.index] = position;
  }
  if (bestTime === null || timeMs < bestTime) {
    run.times[run.index] = timeMs;
  }
}

/**
 * Room-mode counterpart to `recordGrandPrixAttempt`: keeps only the outright
 * best time for the current stage. Never touches `positions`/`points` — in a
 * room, those are computed server-side from real friends' verified times, not
 * from a local AI-relative finish.
 */
export function recordGrandPrixTime(run: GrandPrixRun, timeMs: number): void {
  const best = run.times[run.index];
  if (best === null || timeMs < best) run.times[run.index] = timeMs;
}

/** Sum of every stage's best time, or null while any stage is still unset. */
export function grandPrixTotal(run: GrandPrixRun): number | null {
  if (run.times.some((t) => t === null)) return null;
  return run.times.reduce((sum: number, t) => sum + (t ?? 0), 0);
}

/** Sum of every stage's championship points, or null while any stage is still unset. */
export function grandPrixPointsTotal(run: GrandPrixRun): number | null {
  if (run.points.some((p) => p === null)) return null;
  return run.points.reduce((sum: number, p) => sum + (p ?? 0), 0);
}

export function isLastGrandPrixStage(run: GrandPrixRun): boolean {
  return run.index >= run.stages.length - 1;
}
